import React, { useState } from 'react';
import {
  Loader2,
  X,
  Sparkles,
  AlertTriangle,
  Check,
  Video,
  Users,
  Clock,
} from 'lucide-react';
import { liveAvatarAPI } from '../../services/api';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import { useBilling } from '../../contexts/BillingContext';
import type { ActiveCredit } from '../../types/billing';
import { resolveAvatarMinutes, purchasedMinutesNote } from './liveAvatarMinutes';
import { useToast } from '../../contexts/ToastContext';
import type {
  LiveAvatarTier,
  LiveAvatarProrationPreview,
} from '../../types/billing';
import {
  LIVEAVATAR_TIER_DISPLAY,
  LIVEAVATAR_TIER_ORDER,
  ENTERPRISE_MAILTO,
} from '../../types/billing';
import { formatServerDate } from '../../utils/datetime';

const formatDate = (iso?: string | null) =>
  formatServerDate(iso, undefined, { month: 'short', day: 'numeric', year: 'numeric' });

const formatCurrency = (amountCents: number, currency = 'usd') => {
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format(amountCents / 100);
  } catch {
    return `$${(amountCents / 100).toFixed(2)}`;
  }
};

const tierIndex = (t: LiveAvatarTier) => LIVEAVATAR_TIER_ORDER.indexOf(t);

const usagePercent = (used: number, cap: number): number => {
  if (cap <= 0) return 0;
  return Math.min(100, Math.round((used / cap) * 100));
};

const barColor = (pct: number) => {
  if (pct >= 100) return 'bg-red-500';
  if (pct >= 80) return 'bg-amber-500';
  return 'bg-emerald-500';
};

type ModalMode =
  | { kind: 'subscribe'; tier: LiveAvatarTier }
  | { kind: 'change'; tier: LiveAvatarTier }
  | { kind: 'cancel' };

interface LiveAvatarSectionProps {
  /** Active `liveavatar_minutes` credit rows, from `GET /api/billing/credits`.
   *  Owned by BillingPage (the only caller) so the endpoint is fetched once.
   *  Absent/empty is the normal case — most workspaces buy no bundles. */
  credits?: ActiveCredit[];
}

const LiveAvatarSection: React.FC<LiveAvatarSectionProps> = ({ credits = [] }) => {
  const { activeWorkspace, isOwner } = useWorkspace();
  const { billing, refetch } = useBilling();
  const { showToast } = useToast();

  const [modal, setModal] = useState<ModalMode | null>(null);
  const [preview, setPreview] = useState<LiveAvatarProrationPreview | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [undoingPending, setUndoingPending] = useState(false);

  const addon = billing?.liveavatar_addon ?? null;
  const currentTier = addon?.tier ?? null;
  const isSubscribed = !!currentTier && addon?.status === 'active';
  const hasBaseSubscription =
    billing?.plan !== 'trial' && billing?.subscription_status !== 'incomplete';
  // Plan-InternalWorkspacePolicyV2.md §C.3 (2026-05-15) — internal
  // workspaces self-serve the LA tier picker. Backend (Storm Phase A.4)
  // short-circuits Stripe and writes `liveavatar_addon = {tier, status,
  // source: "internal"}` directly. UI swaps CTA labels ("Apply" instead
  // of "Subscribe" / "Confirm change") and skips the proration preview
  // fetch since the backend won't return one. Internal LA flips are
  // free + instant; cancel-at-period-end semantics still apply (backend
  // sets `status: "canceled"` but keeps the row until next cycle).
  const isInternalWorkspace = !!billing?.is_company_owned;

  const openSubscribeModal = (tier: LiveAvatarTier) => {
    setModal({ kind: 'subscribe', tier });
    setPreview(null);
    setPreviewError(null);
  };

  const openChangeModal = async (tier: LiveAvatarTier) => {
    if (!activeWorkspace) return;
    setModal({ kind: 'change', tier });
    setPreview(null);
    setPreviewError(null);
    // Plan-InternalWorkspacePolicyV2.md §C.3 — skip the proration
    // preview for internal workspaces (backend never returns one;
    // change flow is no-Stripe direct DB write).
    if (isInternalWorkspace) return;
    setPreviewLoading(true);
    try {
      const res = await liveAvatarAPI.previewChange(activeWorkspace.id, tier);
      setPreview(res.data);
    } catch (err: any) {
      setPreviewError(err?.response?.data?.detail || 'Could not load proration preview');
    } finally {
      setPreviewLoading(false);
    }
  };

  const openCancelModal = () => {
    setModal({ kind: 'cancel' });
    setPreview(null);
    setPreviewError(null);
  };

  const closeModal = () => {
    if (confirming) return;
    setModal(null);
    setPreview(null);
    setPreviewError(null);
  };

  const confirmAction = async () => {
    if (!activeWorkspace || !modal) return;
    setConfirming(true);
    try {
      if (modal.kind === 'subscribe') {
        await liveAvatarAPI.subscribe(activeWorkspace.id, modal.tier);
        showToast(`Subscribed to ${LIVEAVATAR_TIER_DISPLAY[modal.tier].name}`, 'success');
      } else if (modal.kind === 'change') {
        const res = await liveAvatarAPI.changeTier(activeWorkspace.id, modal.tier);
        const msg =
          res.data.status === 'upgraded'
            ? `Upgraded to ${LIVEAVATAR_TIER_DISPLAY[modal.tier].name}`
            : res.data.status === 'scheduled'
            ? `Downgrade to ${LIVEAVATAR_TIER_DISPLAY[modal.tier].name} scheduled`
            : 'Tier changed';
        showToast(msg, 'success');
      } else if (modal.kind === 'cancel') {
        await liveAvatarAPI.cancel(activeWorkspace.id);
        showToast('AI Avatar will be canceled at period end', 'success');
      }
      await refetch();
      closeModal();
    } catch (err: any) {
      showToast(err?.response?.data?.detail || 'Action failed', 'error');
    } finally {
      setConfirming(false);
    }
  };

  const handleUndoPending = async () => {
    if (!activeWorkspace) return;
    setUndoingPending(true);
    try {
      await liveAvatarAPI.cancelScheduledChange(activeWorkspace.id);
      showToast('Scheduled change released', 'success');
      await refetch();
    } catch (err: any) {
      showToast(err?.response?.data?.detail || 'Failed to release scheduled change', 'error');
    } finally {
      setUndoingPending(false);
    }
  };

  if (!billing) return null;

  // 🚨 The combined allowance — tier minutes PLUS purchased bundle minutes.
  // Dividing by `addon.minutes_cap` alone is what rendered "72 / 60" to a
  // customer who had bought a 50-minute bundle: since `back a04e767` those
  // minutes extend the cap, so the tier is no longer the denominator.
  const avatarMinutes = resolveAvatarMinutes({
    serverCapTotal: addon?.minutes_cap_total,
    addonMinutesCap: addon?.minutes_cap,
    planLimit: billing.usage_limits?.max_liveavatar_minutes,
    credits,
    used: addon?.minutes_used ?? 0,
  });
  const purchasedNote = purchasedMinutesNote(avatarMinutes);
  const minutesPct = addon ? avatarMinutes.pct : 0;
  const concurrencyPct = addon ? usagePercent(addon.concurrency_active, addon.concurrency_cap) : 0;

  return (
    <section className="card" id="liveavatar">
      <div className="flex items-start justify-between flex-wrap gap-3 mb-5">
        <div className="min-w-0">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <Video className="w-5 h-5 text-indigo-500" />
            AI Avatar Add-On
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 max-w-xl">
            Realistic HD streaming avatars powered by HeyGen. Billed separately on top of your base plan.
          </p>
        </div>
      </div>

      {/* Pending change banner */}
      {addon?.pending_change && (
        <div className="mb-5 rounded-xl border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 p-4 flex items-start justify-between flex-wrap gap-3">
          <div className="flex items-start gap-2 min-w-0">
            <AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />
            <p className="text-sm text-amber-800 dark:text-amber-300">
              {addon.pending_change.target_tier
                ? `Downgrading to ${LIVEAVATAR_TIER_DISPLAY[addon.pending_change.target_tier].name}`
                : 'Canceling AI Avatar add-on'}{' '}
              on <span className="font-semibold">{formatDate(addon.pending_change.effective_at)}</span>
            </p>
          </div>
          <button
            onClick={handleUndoPending}
            disabled={!isOwner || undoingPending}
            className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-white dark:bg-gray-900 text-amber-700 dark:text-amber-300 border border-amber-300 dark:border-amber-700 hover:bg-amber-100 dark:hover:bg-amber-900/40 disabled:opacity-50 flex items-center gap-1.5"
          >
            {undoingPending && <Loader2 className="w-3 h-3 animate-spin" />}
            Undo
          </button>
        </div>
      )}

      {/* Trial guard */}
      {!hasBaseSubscription && !isSubscribed && (
        <div className="mb-5 rounded-xl border border-indigo-200 dark:border-indigo-800 bg-indigo-50 dark:bg-indigo-900/20 p-4 flex items-start gap-2">
          <Sparkles className="w-4 h-4 text-indigo-600 dark:text-indigo-400 mt-0.5 shrink-0" />
          <p className="text-sm text-indigo-800 dark:text-indigo-300">
            Subscribe to a base plan (Starter, Pro, or Business) before adding AI Avatar.
          </p>
        </div>
      )}

      {isSubscribed && addon ? (
        /* ACTIVE SUBSCRIPTION STATE */
        <div className="space-y-5">
          <div className="rounded-xl border-2 border-indigo-200 dark:border-indigo-800 bg-indigo-50/50 dark:bg-indigo-900/10 p-5">
            <div className="flex items-start justify-between flex-wrap gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="font-bold text-gray-900 dark:text-white text-lg">
                    {LIVEAVATAR_TIER_DISPLAY[currentTier!].name}
                  </h3>
                  <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300">
                    Active
                  </span>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  {LIVEAVATAR_TIER_DISPLAY[currentTier!].price}/mo
                  {addon.current_period_end && (
                    <>
                      {' · '}Next renewal:{' '}
                      <span className="font-semibold">{formatDate(addon.current_period_end)}</span>
                    </>
                  )}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => openChangeModal(currentTier!)}
                  disabled={!isOwner}
                  className="px-3 py-1.5 rounded-lg text-xs sm:text-sm font-semibold bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:border-primary disabled:opacity-50"
                >
                  Change tier
                </button>
                <button
                  onClick={openCancelModal}
                  disabled={!isOwner || !!addon.pending_change}
                  className="px-3 py-1.5 rounded-lg text-xs sm:text-sm font-semibold text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>

          {/* Usage bars */}
          <div className="grid sm:grid-cols-2 gap-5">
            <div className="space-y-1.5">
              <div className="flex items-center justify-between text-xs sm:text-sm">
                <span className="font-medium text-gray-700 dark:text-gray-300 flex items-center gap-1.5">
                  <Clock className="w-3.5 h-3.5" /> Minutes used
                </span>
                <span className="text-gray-500 dark:text-gray-400 font-mono text-[11px] sm:text-xs">
                  {Math.round(addon.minutes_used).toLocaleString()} /{' '}
                  {avatarMinutes.unlimited ? '∞' : avatarMinutes.displayCap.toLocaleString()} min
                </span>
              </div>
              <div className="h-2 w-full rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden">
                <div
                  className={`h-full ${barColor(minutesPct)} transition-all`}
                  style={{ width: `${Math.max(minutesPct, addon.minutes_used > 0 ? 3 : 0)}%` }}
                />
              </div>
              {/* Only when bundle minutes exist — the zero-bundle customer, who
                  is most customers, sees exactly what they saw before. */}
              {purchasedNote && (
                <p className="text-[11px] text-gray-500 dark:text-gray-400">{purchasedNote}</p>
              )}
            </div>
            <div className="space-y-1.5">
              <div className="flex items-center justify-between text-xs sm:text-sm">
                <span className="font-medium text-gray-700 dark:text-gray-300 flex items-center gap-1.5">
                  <Users className="w-3.5 h-3.5" /> Concurrent sessions
                </span>
                <span className="text-gray-500 dark:text-gray-400 font-mono text-[11px] sm:text-xs">
                  {addon.concurrency_active} / {addon.concurrency_cap} active
                </span>
              </div>
              <div className="h-2 w-full rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden">
                <div
                  className={`h-full ${barColor(concurrencyPct)} transition-all`}
                  style={{ width: `${Math.max(concurrencyPct, addon.concurrency_active > 0 ? 3 : 0)}%` }}
                />
              </div>
            </div>
          </div>

          {/* Change tier grid (only other tiers) */}
          <div>
            <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">
              Other tiers
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {LIVEAVATAR_TIER_ORDER.filter((t) => t !== currentTier).map((t) => {
                const disp = LIVEAVATAR_TIER_DISPLAY[t];
                const isUpgrade = tierIndex(t) > tierIndex(currentTier!);
                return (
                  <button
                    key={t}
                    onClick={() => openChangeModal(t)}
                    disabled={!isOwner}
                    className="text-left p-4 rounded-xl border-2 border-gray-200 dark:border-gray-700 hover:border-indigo-400 transition-all disabled:opacity-50"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-semibold text-gray-900 dark:text-white text-sm">
                        {disp.name}
                      </span>
                      <span className="text-[10px] uppercase font-bold text-gray-500 dark:text-gray-400">
                        {isUpgrade ? 'Upgrade' : 'Downgrade'}
                      </span>
                    </div>
                    <p className="text-lg font-bold text-gray-900 dark:text-white mt-1">
                      {disp.price}
                      <span className="text-xs text-gray-500">/mo</span>
                    </p>
                    <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">
                      {disp.minutes} min · {disp.concurrency} concurrent
                    </p>
                  </button>
                );
              })}
              <a
                href={ENTERPRISE_MAILTO}
                className="text-left p-4 rounded-xl border-2 border-gray-200 dark:border-gray-700 hover:border-indigo-400 transition-all block"
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="font-semibold text-gray-900 dark:text-white text-sm">
                    Avatar Enterprise
                  </span>
                  <span className="text-[10px] uppercase font-bold text-gray-500 dark:text-gray-400">
                    Contact
                  </span>
                </div>
                <p className="text-lg font-bold text-gray-900 dark:text-white mt-1">Custom</p>
                <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">
                  Negotiated capacity
                </p>
              </a>
            </div>
          </div>
        </div>
      ) : (
        /* NO SUBSCRIPTION STATE — tier comparison grid */
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {LIVEAVATAR_TIER_ORDER.map((t) => {
            const disp = LIVEAVATAR_TIER_DISPLAY[t];
            const isFeatured = t === 'growth';
            return (
              <div
                key={t}
                className={`relative p-5 rounded-xl border-2 transition-all ${
                  isFeatured
                    ? 'border-indigo-400 bg-indigo-50/50 dark:bg-indigo-900/10'
                    : 'border-gray-200 dark:border-gray-700 hover:border-indigo-400/50'
                }`}
              >
                {isFeatured && (
                  <span className="absolute -top-2.5 right-4 bg-indigo-500 text-white text-[10px] font-bold uppercase px-2 py-0.5 rounded-full">
                    Most popular
                  </span>
                )}
                <h3 className="font-bold text-gray-900 dark:text-white">{disp.name}</h3>
                <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                  {disp.price}
                  <span className="text-sm text-gray-500">/mo</span>
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 mb-3">{disp.tagline}</p>
                <ul className="text-xs text-gray-600 dark:text-gray-400 space-y-1.5 mb-4">
                  {disp.perks.map((perk) => (
                    <li key={perk} className="flex items-start gap-1.5">
                      <Check className="w-3.5 h-3.5 text-emerald-500 mt-0.5 shrink-0" />
                      <span>{perk}</span>
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => openSubscribeModal(t)}
                  disabled={!isOwner || !hasBaseSubscription}
                  className={`w-full py-2 rounded-lg text-sm font-semibold transition-all ${
                    isFeatured
                      ? 'bg-gradient-to-r from-indigo-500 to-purple-600 text-white hover:shadow-lg'
                      : 'border-2 border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:border-indigo-400'
                  } disabled:opacity-50 disabled:cursor-not-allowed`}
                  title={
                    !hasBaseSubscription
                      ? 'Subscribe to a base plan first'
                      : !isOwner
                      ? 'Only workspace owners can subscribe'
                      : ''
                  }
                >
                  {isInternalWorkspace ? 'Apply' : 'Subscribe'}
                </button>
              </div>
            );
          })}
          {/* Enterprise card */}
          <div className="relative p-5 rounded-xl border-2 border-gray-200 dark:border-gray-700 hover:border-indigo-400/50 transition-all">
            <h3 className="font-bold text-gray-900 dark:text-white">Avatar Enterprise</h3>
            <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">Custom</p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 mb-3">
              Negotiated scale
            </p>
            <ul className="text-xs text-gray-600 dark:text-gray-400 space-y-1.5 mb-4">
              <li className="flex items-start gap-1.5">
                <Check className="w-3.5 h-3.5 text-emerald-500 mt-0.5 shrink-0" />
                <span>Unlimited minutes</span>
              </li>
              <li className="flex items-start gap-1.5">
                <Check className="w-3.5 h-3.5 text-emerald-500 mt-0.5 shrink-0" />
                <span>Dedicated HeyGen pool</span>
              </li>
              <li className="flex items-start gap-1.5">
                <Check className="w-3.5 h-3.5 text-emerald-500 mt-0.5 shrink-0" />
                <span>Custom avatars + voices</span>
              </li>
              <li className="flex items-start gap-1.5">
                <Check className="w-3.5 h-3.5 text-emerald-500 mt-0.5 shrink-0" />
                <span>SLA + dedicated CSM</span>
              </li>
            </ul>
            <a
              href={ENTERPRISE_MAILTO}
              className="w-full block text-center py-2 rounded-lg text-sm font-semibold bg-gradient-to-r from-primary to-purple-600 text-white hover:shadow-lg"
            >
              Contact sales
            </a>
          </div>
        </div>
      )}

      {/* Modals */}
      {modal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto border border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                {modal.kind === 'subscribe'
                  ? (isInternalWorkspace
                      ? `Apply ${LIVEAVATAR_TIER_DISPLAY[modal.tier].name}`
                      : `Subscribe to ${LIVEAVATAR_TIER_DISPLAY[modal.tier].name}`)
                  : modal.kind === 'change'
                  ? `Change to ${LIVEAVATAR_TIER_DISPLAY[modal.tier].name}`
                  : 'Cancel AI Avatar'}
              </h3>
              <button
                onClick={closeModal}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="px-6 py-5 text-sm text-gray-700 dark:text-gray-300 space-y-3">
              {modal.kind === 'subscribe' && (
                <>
                  {isInternalWorkspace ? (
                    <p>
                      Enable{' '}
                      <span className="font-semibold">
                        {LIVEAVATAR_TIER_DISPLAY[modal.tier].name}
                      </span>{' '}
                      on this workspace? Internal workspaces apply LA
                      tier changes instantly and are not billed.
                    </p>
                  ) : (
                    <p>
                      You'll be charged{' '}
                      <span className="font-semibold">{LIVEAVATAR_TIER_DISPLAY[modal.tier].price}/mo</span>{' '}
                      on top of your base plan. Your next base-plan invoice will include this add-on.
                    </p>
                  )}
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Workspace: <span className="font-semibold text-gray-700 dark:text-gray-200">{activeWorkspace?.name}</span>
                  </p>
                  <div className="bg-gray-50 dark:bg-gray-800/60 rounded-lg p-3 text-xs">
                    {LIVEAVATAR_TIER_DISPLAY[modal.tier].minutes} minutes ·{' '}
                    {LIVEAVATAR_TIER_DISPLAY[modal.tier].concurrency} concurrent sessions
                  </div>
                </>
              )}
              {modal.kind === 'change' && (
                <>
                  {/* Plan-InternalWorkspacePolicyV2.md §C.3 — internal
                      workspace LA tier change: no preview to fetch
                      (skipped in openChangeModal), render a static
                      apply-instantly panel instead of the proration
                      lines. */}
                  {isInternalWorkspace ? (
                    <div className="space-y-3">
                      <p>
                        Switch this workspace's AI Avatar add-on to{' '}
                        <span className="font-semibold">
                          {LIVEAVATAR_TIER_DISPLAY[modal.tier].name}
                        </span>
                        ?
                      </p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        Workspace: <span className="font-semibold text-gray-700 dark:text-gray-200">{activeWorkspace?.name}</span>
                      </p>
                      <div className="rounded-lg border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20 px-3 py-2.5 text-xs text-blue-800 dark:text-blue-200">
                        Internal workspaces apply LA tier changes
                        immediately and are not billed.
                      </div>
                    </div>
                  ) : (
                  <>
                  {previewLoading && (
                    <div className="flex items-center gap-2 text-gray-500">
                      <Loader2 className="w-4 h-4 animate-spin" /> Calculating proration…
                    </div>
                  )}
                  {previewError && !previewLoading && (
                    <p className="text-red-600 dark:text-red-400">{previewError}</p>
                  )}
                  {preview && !previewLoading && (
                    <div className="space-y-3">
                      <p>
                        Change your AI Avatar add-on to{' '}
                        <span className="font-semibold">
                          {LIVEAVATAR_TIER_DISPLAY[modal.tier].name}
                        </span>
                        .
                      </p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        Workspace: <span className="font-semibold text-gray-700 dark:text-gray-200">{activeWorkspace?.name}</span>
                      </p>
                      <div className="bg-gray-50 dark:bg-gray-800/60 rounded-lg p-4 space-y-1.5">
                        {preview.lines.map((line, i) => (
                          <div key={i} className="flex justify-between text-xs">
                            <span className="text-gray-600 dark:text-gray-400 truncate pr-2">
                              {line.description}
                            </span>
                            <span className="font-mono text-gray-900 dark:text-white">
                              {formatCurrency(line.amount, preview.currency)}
                            </span>
                          </div>
                        ))}
                        <div className="border-t border-gray-200 dark:border-gray-700 pt-2 mt-2 flex justify-between">
                          <span className="font-semibold text-gray-900 dark:text-white">
                            Amount due
                          </span>
                          <span className="font-bold text-gray-900 dark:text-white">
                            {formatCurrency(preview.amount_due, preview.currency)}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                  </>
                  )}
                </>
              )}
              {modal.kind === 'cancel' && (
                <>
                  <p>
                    AI Avatar will stay active until{' '}
                    <span className="font-semibold">
                      {formatDate(addon?.current_period_end)}
                    </span>
                    , then cancel. You can undo anytime before then.
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Workspace: <span className="font-semibold text-gray-700 dark:text-gray-200">{activeWorkspace?.name}</span>
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Unused minutes are forfeited on cancellation — they do not carry over.
                  </p>
                </>
              )}
            </div>
            <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
              <button
                onClick={closeModal}
                className="px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 font-medium text-sm"
              >
                Cancel
              </button>
              <button
                onClick={confirmAction}
                disabled={confirming || (modal.kind === 'change' && previewLoading)}
                className={`px-4 py-2 rounded-lg text-white font-semibold text-sm disabled:opacity-50 flex items-center justify-center gap-2 ${
                  modal.kind === 'cancel'
                    ? 'bg-red-600 hover:bg-red-700'
                    : 'bg-gradient-to-r from-indigo-500 to-purple-600 hover:shadow-lg'
                }`}
              >
                {confirming && <Loader2 className="w-4 h-4 animate-spin" />}
                {modal.kind === 'subscribe'
                  ? (isInternalWorkspace ? 'Apply tier' : 'Confirm subscribe')
                  : modal.kind === 'change'
                  ? (isInternalWorkspace ? 'Apply tier change' : 'Confirm change')
                  : 'Confirm cancel'}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default LiveAvatarSection;
